import { map } from 'lodash';

import * as api from '../services/API';
import { actions } from '../ducks/Cards';
import onError from './Error';
import Immutable from 'seamless-immutable';
import { Dispatch } from 'redux';
import { Comment } from '@/models/CommentModel';
import { Card } from '@/models/card/CardModel';
import { Board } from '@/models/BoardModel';
import { User } from '@/models/permission/AuthorityModel';

export const fetchCards = ({
  communityId,
  roomId,
  boardId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.fetchCardsRequest({ boardId }));

    try {
      const cards = await api.fetchCards({ communityId, roomId, boardId });

      return dispatch(actions.fetchCardsSuccess({ cards }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.fetchCardsError({ error }) });
    }
  };
};

export const updateCard = ({
  communityId,
  roomId,
  boardId,
  cardId,
  data,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
  data: Card;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.updateCardRequest());

    try {
      const card = await api.updateCard({ communityId, roomId, boardId, cardId, data });

      return dispatch(actions.updateCardSuccess({ card: card }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.updateCardError({ error }) });
    }
  };
};

export const updateCardImmediately = ({
  communityId,
  roomId,
  boardId,
  card,
  stepId,
  data,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  card: Card;
  stepId: string;
  data: any[];
}) => {
  return async (dispatch: Dispatch) => {
    let updatedCard = {
      ...card,
      fields: card.fields.map((field) => {
        const fields = data.filter((updatedField) => updatedField.id === field.id);
        return fields.length ? Immutable(fields[0]) : field;
      }),
      fakeModel: true,
    };
    const targetStep = {
      id: stepId,
    };

    if (stepId) {
      updatedCard = {
        ...updatedCard,
        step: targetStep,
      };
    }

    dispatch(actions.updateCardImmediatelySuccess({ card: updatedCard }));

    try {
      const cardFromBackend = await api.updateCard({
        communityId,
        roomId,
        boardId,
        cardId: card.id,
        stepId,
        data: {
          type: 'entry',
          fields: data,
          step: card.step,
        },
      });

      return dispatch(actions.updateCardSuccess({ card: cardFromBackend }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.updateCardImmediatelyError({ error, card }) });
    }
  };
};

export const lockCard = ({
  communityId,
  roomId,
  boardId,
  cardId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.lockCardRequest());

    try {
      const card = await api.lockCard({ communityId, roomId, boardId, cardId });

      return dispatch(actions.lockCardSuccess({ card }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.lockCardError({ error }) });
    }
  };
};

export const unlockCard = ({
  communityId,
  roomId,
  boardId,
  cardId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.unlockCardRequest());

    try {
      const card = await api.unlockCard({ communityId, roomId, boardId, cardId });

      return dispatch(actions.unlockCardSuccess({ card: card }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.unlockCardError({ error }) });
    }
  };
};

export const createCard = ({
  communityId,
  roomId,
  boardId,
  data,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  data: Card;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.createCardRequest());

    try {
      const card = await api.createCard({ communityId, roomId, boardId, data });

      return dispatch(actions.createCardSuccess({ card: card }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.createCardError({ error }) });
    }
  };
};

export const moveCardImmediately = ({
  communityId,
  roomId,
  board,
  card,
  stepId,
}: {
  communityId: string;
  roomId: string;
  board: Board;
  card: Card;
  stepId: string;
}) => {
  return async (dispatch: Dispatch) => {
    const updatedCard = card.merge({
      step: { id: stepId },
      fakeModel: true,
    });

    dispatch(actions.updateCardImmediatelySuccess({ card: updatedCard }));

    try {
      const cardFromBackend = await api.moveCard({ communityId, roomId, boardId: board.id, cardId: card.id, stepId });

      return dispatch(actions.updateCardSuccess({ card: cardFromBackend }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.updateCardImmediatelyError({ error, card }) });
    }
  };
};

export const moveCard = ({
  communityId,
  roomId,
  board,
  cardId,
  stepId,
}: {
  communityId: string;
  roomId: string;
  board: Board;
  cardId: string;
  stepId: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.updateCardRequest());

    try {
      const card = await api.moveCard({ communityId, roomId, boardId: board.id, cardId, stepId });

      return dispatch(actions.updateCardSuccess({ card }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.updateCardError({ error }) });
    }
  };
};

export const fetchAttachments = ({
  communityId,
  roomId,
  boardId,
  cardId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.fetchAttachmentsRequest());

    try {
      const attachments = await api.fetchCardAttachments({ communityId, roomId, boardId, cardId });

      return dispatch(actions.fetchAttachmentsSuccess({ attachments }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.fetchAttachmentsError({ error }) });
    }
  };
};

export const uploadAttachment = ({
  communityId,
  roomId,
  boardId,
  cardId,
  files,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
  files: File[];
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.uploadAttachmentRequest());

    try {
      const uploads = [];
      const attachUpload = (file) =>
        uploads.push(api.uploadCardAttachment({ communityId, roomId, boardId, cardId, file }));
      map(files, attachUpload);
      const attachments = await Promise.all(uploads);

      return dispatch(actions.uploadAttachmentSuccess({ cardId, attachments }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.uploadAttachmentError({ error }) });
    }
  };
};

export const addLink = ({
  communityId,
  roomId,
  boardId,
  cardId,
  link,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
  link: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.addLinkRequest());

    try {
      const createdLink = await api.addLink({ communityId, roomId, boardId, cardId, link });

      return dispatch(actions.addLinkSuccess({ cardId, link: createdLink }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.addLinkError({ error }) });
    }
  };
};

export const deleteAttachment = ({
  communityId,
  roomId,
  boardId,
  cardId,
  attachmentId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
  attachmentId: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.deleteAttachmentsRequest());

    try {
      await api.deleteCardAttachment({ communityId, roomId, boardId, cardId, attachmentId });

      return dispatch(actions.deleteAttachmentsSuccess({ cardId, attachmentId }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.deleteAttachmentsError({ error }) });
    }
  };
};

export const createComment = ({
  communityId,
  roomId,
  boardId,
  cardId,
  content,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
  content: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.createCommentRequest());

    try {
      const comment = await api.createCardComment({ communityId, roomId, boardId, cardId, content });
      comment.cardId = cardId;

      return dispatch(actions.createCommentSuccess({ comment }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.createCommentError({ error }) });
    }
  };
};

export const updateComment = ({
  communityId,
  roomId,
  boardId,
  cardId,
  comment,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
  comment: Comment;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.updateCommentRequest());

    try {
      const updatedComment = await api.updateCardComment({ communityId, roomId, boardId, cardId, comment });
      updatedComment.cardId = cardId;

      return dispatch(actions.updateCommentSuccess({ comment: updatedComment }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.updateCommentError({ error }) });
    }
  };
};

export const deleteComment = ({
  communityId,
  roomId,
  boardId,
  cardId,
  commentId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
  commentId: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.deleteCommentRequest());

    try {
      await api.deleteCardComment({ communityId, roomId, boardId, cardId, commentId });

      return dispatch(actions.deleteCommentSuccess({ commentId: commentId }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.deleteCommentError({ error }) });
    }
  };
};

export const fetchComments = ({
  communityId,
  roomId,
  boardId,
  cardId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.fetchCommentsRequest());

    try {
      const comments = await api.fetchCardComments({ communityId, roomId, boardId, cardId });
      comments.forEach((comment: Comment) => {
        comment.cardId = cardId;
      });

      return dispatch(actions.fetchCommentsSuccess({ comments }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.fetchCommentsError({ error }) });
    }
  };
};

export const fetchLog = ({
  communityId,
  roomId,
  boardId,
  cardId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  cardId: string;
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.fetchLogRequest());

    try {
      const log = await api.fetchCardLog({ communityId, roomId, boardId, cardId });

      return dispatch(actions.fetchLogSuccess({ log }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.fetchLogError({ error }) });
    }
  };
};

export const fetchPossibleResponsible = ({
  communityId,
  roomId,
  boardId,
  disciplines,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  disciplines: string[];
}) => {
  return async (dispatch: Dispatch) => {
    dispatch(actions.fetchPossibleResponsibleRequest({ boardId }));

    try {
      let possibleResponsible: User[] = [];

      if (disciplines.length > 0) {
        possibleResponsible = await api.getRoomMembers({ communityId, roomId, boardId, disciplines });
      }

      return dispatch(actions.fetchPossibleResponsibleSuccess({ possibleResponsible }));
    } catch (error) {
      return onError({ error, dispatch, action: actions.fetchPossibleResponsibleError({ error }) });
    }
  };
};
